import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Footer from '@/components/feature/Footer';

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于2048核基地',
    description: '了解2048核基地的品牌故事、核心团队和发展历程。我们致力于为全球2048玩家提供最专业的策略攻略。',
    url: 'https://pmt-pbt.cn/about',
    about: {
      '@type': 'Organization',
      name: '2048核基地',
      description: '2048核基地是专为2048游戏深度玩家打造的策略攻略与挑战平台。',
      foundingDate: '2024',
    },
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="了解2048核基地的品牌故事、核心团队和发展历程。我们致力于为全球2048玩家提供最专业的策略攻略。"
        keywords="关于2048核基地,品牌介绍,攻略团队"
        canonicalUrl="https://pmt-pbt.cn/about"
        ogType="website"
        schema={aboutSchema}
      />
      <Navbar />
      <main className="pt-20 pb-20">
        <div className="px-4 md:px-6 max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 mb-4 mt-4">
            关于2048核基地
          </h1>
          <p className="text-base text-foreground-600 mb-12 leading-relaxed max-w-2xl">
            了解我们的品牌故事、核心团队和使命愿景
          </p>

          <section className="mb-16">
            <div className="aspect-[21/9] rounded-xl overflow-hidden mb-8">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20minimalist%20geometric%20composition%20with%20warm%20golden%20amber%20tones%2C%20floating%20squares%20representing%20digital%20puzzle%20pieces%20coming%20together%2C%20clean%20cream%20background%2C%20modern%20teamwork%20concept%2C%20soft%20ambient%20light%2C%20editorial%20quality&width=1200&height=514&seq=about-brand-01&orientation=landscape"
                alt="2048核基地品牌形象"
                className="w-full h-full object-cover"
              />
            </div>
            <h2 className="text-2xl font-bold text-foreground-900 mb-5">品牌介绍</h2>
            <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
              <p>
                <strong>2048核基地</strong>成立于2024年，是国内最早专注于2048游戏策略研究的专业平台之一。我们的创始团队由资深2048玩家、游戏策略分析师和教育内容创作者组成，拥有超过10年的益智游戏研究经验。
              </p>
              <p>
                在2048游戏风靡全球的浪潮中，我们发现大多数攻略内容停留在表面——告诉玩家"该怎么做"，却不解释"为什么这样做"。2048核基地的诞生，就是为了填补这一空白：用系统化的方法论和深度的策略分析，帮助每一位玩家真正理解2048的内在逻辑。
              </p>
              <p>
                经过两年多的发展，2048核基地已积累了<strong>超过18篇深度策略文章</strong>，涵盖核心玩法、高分技巧、合并策略、进阶秘籍和极限挑战五大领域。我们的内容被无数2048玩家社群推荐和引用，成为中文2048攻略的标杆。
              </p>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-2xl font-bold text-foreground-900 mb-5">口碑与影响力</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-primary-500 mb-2">98%</div>
                <p className="text-sm text-foreground-600">读者满意度</p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-primary-500 mb-2">50万+</div>
                <p className="text-sm text-foreground-600">累计阅读量</p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-primary-500 mb-2">18+</div>
                <p className="text-sm text-foreground-600">深度攻略文章</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                <p className="text-sm text-foreground-700 italic mb-2">
                  "2048核基地的策略分析是我见过最深入的。他们的蛇形合并法文章彻底改变了我的游戏方式，让我从5000分直接突破到了20000+。"
                </p>
                <p className="text-xs text-foreground-500">—— 资深玩家 @数字猎人</p>
              </div>
              <div className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                <p className="text-sm text-foreground-700 italic mb-2">
                  "作为一名游戏攻略博主，2048核基地是我最常引用的中文2048资源。他们的内容既有理论深度，又通俗易懂。"
                </p>
                <p className="text-xs text-foreground-500">—— 游戏博主 @策略大师兄</p>
              </div>
              <div className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                <p className="text-sm text-foreground-700 italic mb-2">
                  "终于有人系统化地讲解2048了！从数学原理到实战技巧，每一篇都干货满满。强烈推荐给所有想突破瓶颈的玩家。"
                </p>
                <p className="text-xs text-foreground-500">—— 2048爱好者 @方块达人</p>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-2xl font-bold text-foreground-900 mb-5">联系我们</h2>
            <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-4">联系方式</h3>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3 text-sm text-foreground-600">
                      <div className="w-9 h-9 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                        <i className="ri-mail-line text-primary-600"></i>
                      </div>
                      <span>contact@pmt-pbt.cn</span>
                    </li>
                    <li className="flex items-center gap-3 text-sm text-foreground-600">
                      <div className="w-9 h-9 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                        <i className="ri-wechat-line text-primary-600"></i>
                      </div>
                      <span>微信公众号：2048核基地</span>
                    </li>
                    <li className="flex items-center gap-3 text-sm text-foreground-600">
                      <div className="w-9 h-9 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                        <i className="ri-weibo-line text-primary-600"></i>
                      </div>
                      <span>微博：@2048核基地官方</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-4">给我们留言</h3>
                  <form
                    data-readdy-form="true"
                    action="https://readdy.ai/api/form/d9phta6b4qjhggaepuq0"
                    method="POST"
                    className="space-y-3"
                  >
                    <div>
                      <input
                        type="text"
                        name="name"
                        placeholder="您的称呼"
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                        required
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                        required
                      />
                    </div>
                    <div>
                      <textarea
                        name="message"
                        placeholder="请输入您的留言（限500字）"
                        maxLength={500}
                        rows={3}
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        required
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="spam-guard"
                    />
                    <button
                      type="submit"
                      className="w-full px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer"
                    >
                      发送留言
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}